import React, { useEffect } from "react"
import { Link, graphql, navigate } from "gatsby"

import ReactTooltip from "react-tooltip"
import { InformationCircleIcon } from "@heroicons/react/outline"

import withLocation from "../components/common/withlocation"
import SponsorLink from "../components/promotions/sponsorLink"
import Footer from "../components/common/footer"
import SEO from "../components/common/seo"
import CommunitiesTable from "../components/community/communitiestable"
import HeroSection from "../components/home/HeroSection"
import StatsStrip from "../components/home/StatsStrip"
import HowItWorks from "../components/home/HowItWorks"
import DirectoryImpact from "../components/home/DirectoryImpact"
import SubmitSection from "../components/home/SubmitSection"
import UseCaseSection from "../components/home/UseCaseSection"

import { sortCommunities } from "../utils/communities"
import { getSchemaForHomepage } from "../utils/schema"

import "../styles/home.css"

const IndexPage = ({ data }) => {
  const isMobile = typeof window !== "undefined" && window.screen.width < 600

  useEffect(() => {
    const getRevealEls = () => document.querySelectorAll(".reveal")
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      getRevealEls().forEach(el => el.classList.add("visible"))
      return undefined
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { root: null, rootMargin: "0px 0px -6% 0px", threshold: 0.1 }
    )
    getRevealEls().forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const navigation = [
    { name: "Topics", href: "/topics/" },
    { name: "Communities", href: "/communities/" },
    { name: "Platforms", href: "/platforms/" },
    { name: "Add Community", href: "/submit/", button: false },
    { name: "Advertise", href: "/partners/community/" },
  ]

  const lastUpdated = data.communities.nodes[0]?.updated ?? ""

  const communityCount = data.communities.totalCount
  const topicCount = data.topics.totalCount
  const platformCount = data.platforms.totalCount

  return (
    <div className="bg-gray-50">
      <SEO
        title="Find The Best Online Communities | Hive Index"
        description={`Browse ${communityCount.toLocaleString()}+ online communities on Discord, Slack, Reddit, Circle, and ${platformCount}+ platforms. Find the right community by topic, platform, or features. Free to browse and list.`}
        path="/"
        schema={getSchemaForHomepage()}
      />

      <HeroSection
        navigation={navigation}
        featuredCommunities={data.featuredCommunities.nodes}
        communityTotalCount={data.communities.totalCount}
        isMobile={isMobile}
      />

      <main className="relative py-12 sm:py-16">
        <div className="max-w-7xl mx-auto pb-6 px-4 sm:px-6 lg:pb-16 lg:px-8">
          {/* Stats + mission statement */}
          <StatsStrip
            topicCount={topicCount}
            communityCount={communityCount}
            platformCount={platformCount}
            lastUpdated={lastUpdated}
          />

          {/* Optional sponsor strip */}
          <SponsorLink location="home" canHide={false}>
            {({ name, url, descriptor, onClick }) => (
              <div className="max-w-5xl mt-6 mx-auto p-3 sm:p-4 mb-8 rounded-xl bg-rose-600 text-white font-semibold shadow-sm flex items-center">
                <div>
                  Sponsored this month by{" "}
                  <a
                    href={url}
                    className="underline"
                    onClick={onClick}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                  >
                    {name} - {descriptor}
                  </a>
                </div>
              </div>
            )}
          </SponsorLink>

          {/* How to use the directory: topics, platforms, features */}
          <HowItWorks
            isMobile={isMobile}
            topicNodes={data.topics.nodes}
            topicTotalCount={data.topics.totalCount}
            platformNodes={data.platforms.nodes}
            platformTotalCount={data.platforms.totalCount}
            featureNodes={data.features.nodes}
          />

          {/* Who uses Hive Index — persona cards */}
          <UseCaseSection />

          {/* Where people spend their time in the directory (bar chart) */}
          {/* <DirectoryImpact topicNodes={data.topics.nodes} /> */}

          {/* Community builder CTA */}
          <SubmitSection communityCount={communityCount} />

          {/* Editor's picks — sponsored communities */}
          <section
            className="mt-12 sm:mt-16 reveal"
            aria-labelledby="explore-communities-heading"
          >
            <header className="relative text-center max-w-3xl mx-auto">
              <p className="section-eyebrow">Editor&apos;s picks</p>
              <h2
                id="explore-communities-heading"
                className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl"
              >
                The best communities right now
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Handpicked highlights from across the directory.
              </p>
            </header>

            {data.sponsoredCommunities.nodes.length ? (
              <div className="p-4 sm:p-6 mt-8">
                <div className="relative">
                  <div
                    className="absolute inset-0 flex items-center"
                    aria-hidden="true"
                  >
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex items-center justify-between flex-wrap gap-2">
                    <ReactTooltip
                      id="top-picks-tooltip"
                      place="right"
                      effect="solid"
                      backgroundColor="black"
                      textColor="white"
                      className="w-60"
                    />
                    <div className="pr-3 bg-gray-50 text-lg font-semibold text-gray-900 flex items-center">
                      <div className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 mr-2 text-yellow-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                          />
                        </svg>
                        <span>Today&apos;s top picks</span>
                      </div>
                      <div
                        data-for="top-picks-tooltip"
                        data-tip="These are some of our favorite communities listed in the directory. Select communities displayed here could be advertising partners."
                      >
                        <InformationCircleIcon
                          className="ml-2 h-5 w-5 text-gray-400 cursor-pointer"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-1">
                  <CommunitiesTable
                    communities={sortCommunities(
                      "Oldest",
                      data.sponsoredCommunities.nodes
                    )}
                    showPlatform={true}
                    showTopics={true}
                    linkModal={false}
                    // showVisitors={true}
                  />
                </div>
              </div>
            ) : null}

            {/* Bottom search CTA */}
            <div className="mx-auto max-w-2xl mt-12">
              <form
                onSubmit={event => {
                  event.preventDefault()
                  const el = document.getElementById("searchfield-bottom")
                  const value = el && el.value
                  if (value && value !== "") {
                    navigate(`/search/?keyword=${encodeURIComponent(value)}`)
                  }
                }}
                className="sm:max-w-lg sm:w-full sm:flex mx-auto flex-col sm:flex-row gap-0 sm:gap-3"
              >
                <div className="min-w-0 flex-1">
                  <label htmlFor="searchfield-bottom" className="sr-only">
                    Search topics and communities
                  </label>
                  <input
                    id="searchfield-bottom"
                    name="search"
                    className="block w-full border border-gray-300 rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                    placeholder={`Search all ${communityCount.toLocaleString()}+ communities`}
                    type="search"
                    autoComplete="off"
                    spellCheck="off"
                  />
                </div>
                <div className="mt-4 sm:mt-0 sm:flex-shrink-0">
                  <button
                    type="submit"
                    className="block w-full rounded-md border border-transparent px-5 py-3 bg-rose-600 text-base font-medium text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 sm:px-10 transition-colors"
                  >
                    Search directory
                  </button>
                </div>
              </form>
              <p className="mt-4 text-center text-sm text-gray-500">
                Trusted by thousands of community builders and seekers
                worldwide.
              </p>
              <div className="pb-6 pt-2 mx-auto text-center">
                <Link
                  to="/topics/"
                  className="inline-block text-gray-700 font-semibold text-sm hover:text-rose-600"
                >
                  Or browse all {topicCount} community topics
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export const query = graphql`
  query {
    topics: allGoogleSpreadsheetSourceTopics(
      limit: 20
      sort: { fields: count, order: DESC }
      filter: { count: { gt: 0 } }
    ) {
      totalCount
      nodes {
        name
        slug
        description
        count
        image
        people
        views
        viewsWithCommunities
        viewsWithCommunitiesPrev
      }
    }
    platforms: allGoogleSpreadsheetSourcePlatforms(
      sort: { fields: count, order: DESC }
    ) {
      totalCount
      nodes {
        slug
        name
        count
        logoBig
        groupName
        groupNamePlural
        promoted
      }
    }
    features: allGoogleSpreadsheetSourceFeatures(
      sort: { fields: count, order: DESC }
    ) {
      totalCount
      nodes {
        slug
        name
        order
        sentenceEnding
        count
        icon
        iconActive
        prefix
      }
    }
    communities: allGoogleSpreadsheetSourceCommunities(
      sort: { fields: order, order: DESC }
      limit: 8
      filter: { removed: { ne: true } }
    ) {
      totalCount
      nodes {
        name
        slug
        updated(formatString: "MM/DD/YY")
        members
        established
        description
        topics
        platform
        logo
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        courses
        tools
        token
        claimed
      }
    }
    featuredCommunities: allGoogleSpreadsheetSourceCommunities(
      limit: 18
      sort: { fields: reviewCount, order: DESC }
      filter: { logo: { ne: null }, paid: { eq: true }, removed: { ne: true } }
    ) {
      totalCount
      nodes {
        name
        slug
        updated(formatString: "MM/DD/YY")
        members
        established
        description
        topics
        platform
        logo
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        courses
        tools
        token
        reviewCount
        reviewAvg
        claimed
      }
    }
    sponsoredCommunities: allGoogleSpreadsheetSourceCommunities(
      limit: 20
      filter: { sponsored: { eq: true }, removed: { ne: true } }
    ) {
      totalCount
      nodes {
        name
        slug
        updated(formatString: "MM/DD/YY")
        members
        established
        description
        topics
        platform
        logo
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        courses
        tools
        token
        reviewCount
        reviewAvg
        visitors
        views
        redirects
        claimed
      }
    }
  }
`

export default withLocation(IndexPage)
